import { Clipboard, FileText, ShieldCheck } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';

export type MailTemplate = {
  id: number;
  titre: string;
  apercu: string;
  objet: string;
  content: React.ReactNode;
};

type Props = {
  mailTemplates: MailTemplate[];
  onBackToSelection?: () => void;
  backButtonText?: string;
  showQualificationWarningForTemplate?: number;
  companyDefaultId?: number | null;
};

function SciMailTemplates({
  mailTemplates,
  onBackToSelection,
  backButtonText = '← Retour à la sélection',
  showQualificationWarningForTemplate,
  companyDefaultId,
}: Props) {
  const initialSelectedId = companyDefaultId
    && mailTemplates.some(t => t.id === companyDefaultId) ? companyDefaultId : null;
  const [selectedMailTemplate, setSelectedMailTemplate] = useState<number | null>(initialSelectedId);

  useEffect(() => {
    if (!selectedMailTemplate) {
      setSelectedMailTemplate(initialSelectedId ?? 1);
    }
  }, [selectedMailTemplate, initialSelectedId]);

  return (
    <div className="h-full">
      <div className="mb-4 flex justify-end">
        <Link
          to={ROUTES.COMMUNICATION_SCI_UNSUBSCRIBERS}
          className="rounded bg-rgpd-blue px-4 py-2 text-sm text-white transition hover:bg-blue-700"
        >
          Voir la liste des désinscriptions SCI
        </Link>
      </div>
      <div className="flex h-full gap-6">
        <div className="w-1/3 rounded-lg bg-white p-4 shadow">

        <div className="space-y-2">
          {mailTemplates.map((template) => {
            const isSelected = selectedMailTemplate === template.id;
            const isCompanyDefault = companyDefaultId === template.id;
            return (
              <div
                key={template.id}
                onClick={() => setSelectedMailTemplate(template.id)}
                className={`relative cursor-pointer rounded p-4 transition-all ${
                  isSelected
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 text-gray-800 hover:bg-gray-100'
                }`}
              >
                {isCompanyDefault && (
                  <span className="absolute -top-2 left-2 inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 shadow-sm">
                    <span role="img" aria-label="Étoile">★</span>
                    <span>Modèle par défaut</span>
                  </span>
                )}
                <h4 className="mb-1 font-semibold">Modèle {template.id}</h4>
                <p className={`text-sm ${isSelected ? 'text-blue-100' : 'text-gray-600'}`}>
                  {template.titre}
                </p>
              </div>
            );
          })}
        </div>

        {companyDefaultId !== undefined && (
          <div className="mt-4 rounded-md border border-amber-100 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-700">
            <Link
              to={`${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?tab=contenu`}
              className="font-medium underline-offset-2 hover:underline"
            >
              Modifier dans Paramétrage › Contenu croissance →
            </Link>
          </div>
        )}

        <div className="mt-6 border-t pt-6">
          <button
            onClick={() => { if (onBackToSelection) { onBackToSelection(); } }}
            className="w-full rounded bg-gray-200 py-2 text-sm transition hover:bg-gray-300"
          >
            {backButtonText}
          </button>
        </div>
      </div>

      <div className="flex-1 rounded-lg bg-white p-6 shadow">
        {selectedMailTemplate ? (
          <div>
            {showQualificationWarningForTemplate === selectedMailTemplate && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Attention :</span> L'envoi par email est uniquement autorisé si le contact a été qualifié, c'est-à-dire s'il s'agit d'un représentant légal de la SCI.
                </p>
              </div>
            )}

            <div className="mb-6 flex items-start justify-between">
              <h3 className="text-xl font-semibold text-gray-800">
                {mailTemplates.find((t) => t.id === selectedMailTemplate)?.titre}
              </h3>
              <button
                onClick={async() => {
                  try {
                    const container = document.querySelector('#sci-mail-preview');
                    if (!container) {
                      showToast('error', 'Impossible de copier le contenu.');
                      return;
                    }

                    const extractTextWithBreaks = (element: Element): string => {
                      let result = '';

                      const subjectDiv = element.querySelector('div:first-child');
                      if (subjectDiv) {
                        const subjectLabel = subjectDiv.querySelector('p.font-semibold');
                        const subjectContent = subjectDiv.querySelector('p:not(.font-semibold)');
                        if (subjectLabel && subjectContent) {
                          result += `${subjectLabel.textContent || ''}\n`;
                          result += `${subjectContent.textContent || ''}\n\n`;
                        }
                      }

                      const contentDiv = element.querySelector('.pt-4');
                      if (contentDiv) {
                        const children = [...contentDiv.children];
                        for (let i = 0; i < children.length; i++) {
                          const child = children[i];
                          if (child.tagName === 'P') {
                            const text = (child as HTMLElement).innerHTML
                              .replaceAll(/<br\s*\/?>/gi, '\n')
                              .replaceAll(/<strong>(.*?)<\/strong>/gi, '$1')
                              .replaceAll(/<mark[^>]*>(.*?)<\/mark>/gi, '$1')
                              .replaceAll(/<[^>]+>/g, '');
                            result += `${text}\n\n`;
                          } else if (child.tagName === 'UL') {
                            const listItems = [...(child as HTMLElement).querySelectorAll('li')];
                            for (const item of listItems) {
                              result += `• ${item.textContent || ''}\n`;
                            }
                            result += '\n';
                          }
                        }
                      }

                      return result;
                    };

                    const text = extractTextWithBreaks(container);
                    await navigator.clipboard.writeText(text.trim());
                    showToast('success', 'Contenu copié dans le presse-papiers.');
                  } catch {
                    showToast('error', 'Impossible de copier le contenu.');
                  }
                }}
                className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
                title="Copier le contenu"
              >
                <Clipboard className="h-4 w-4" />
                Copier
              </button>
            </div>

            <div id="sci-mail-preview" className="rounded bg-gray-50 p-6">
              {mailTemplates.find((t) => t.id === selectedMailTemplate)?.content}
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-gray-400">
            <div className="text-center">
              <FileText className="mx-auto mb-4 h-16 w-16" />
              <p>Sélectionnez un modèle pour voir l'aperçu complet</p>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default SciMailTemplates;
